import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { RootState } from '../../store/store';
import { getValorCotaStatus } from '../../api/rateio/rateioApi';
import { getCotaSyncStatus } from '../../api/sga-cota/sga-cotaApi';
import { getProdutoSyncStatus } from '../../api/sga-produto/sga-produtoApi';
import { pendenciasTemplates } from '../../api/termo-template/termoTemplateApi';
import { pendenciasPlanosCotacao } from '../../api/plano-cotacao/planoCotacaoApi';
import { getFipeStatus } from '../../api/veiculos/veiculoApi';

// Sincronizações rodam diariamente (produtos 6h30, cotas 7h, rateio 8h);
// mais de 48h sem sucesso = duas janelas perdidas → pendência
const LIMITE_DEFASAGEM_MS = 48 * 60 * 60 * 1000;

const formatCompetencia = (iso: string) => {
  const [ano, mes] = iso.slice(0, 10).split('-');
  return `${mes}/${ano}`;
};

const defasadoPorTempo = (iso: string | null) =>
  !iso || Date.now() - new Date(iso).getTime() > LIMITE_DEFASAGEM_MS;

// Banner de pendências das sincronizações SGA — visível só para TI/ADMIN.
// Rateio: competência sem valor a partir do dia 10. Cotas/Produtos: última
// sincronização com falha/interrompida ou há mais de 48h (ou nunca executada).
const PendenciasBanner: React.FC = () => {
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const userType = useSelector((state: RootState) => state.auth.userType);
  const location = useLocation();
  const navigate = useNavigate();

  const [pendencias, setPendencias] = useState<string[]>([]);

  const elevado = userType === 'TI' || userType === 'ADMIN';

  const avaliar = useCallback(async () => {
    if (!token) return;
    const encontradas: string[] = [];

    try {
      const rateio = await getValorCotaStatus();
      if (!rateio.temValorNoMes && new Date().getDate() >= 10) {
        encontradas.push(
          `Rateio de ${formatCompetencia(rateio.competenciaCorrente)} sem valor atualizado ` +
            `(em uso: R$ ${rateio.valorEmUso.toFixed(2).replace('.', ',')} de ${formatCompetencia(rateio.competenciaDoValor)})`
        );
      }
    } catch {
      /* status indisponível não vira pendência */
    }

    try {
      const cotas = await getCotaSyncStatus();
      const execucao = cotas.execucao;
      if (execucao?.status === 'FALHA' || execucao?.status === 'INTERROMPIDO') {
        encontradas.push('Sincronização de cotas com falha na última execução');
      } else if (execucao?.status !== 'EM_ANDAMENTO' && defasadoPorTempo(cotas.ultimaSincronizacao)) {
        encontradas.push('Cotas sem sincronização há mais de 48h');
      }
    } catch {
      /* idem */
    }

    try {
      const produtos = await getProdutoSyncStatus();
      const execucao = produtos.execucao;
      if (execucao?.status === 'FALHA' || execucao?.status === 'INTERROMPIDO') {
        encontradas.push('Sincronização de produtos com falha na última execução');
      } else if (
        execucao?.status !== 'EM_ANDAMENTO' &&
        defasadoPorTempo(execucao?.finalizadoEm ?? null)
      ) {
        encontradas.push('Produtos sem sincronização há mais de 48h');
      }
    } catch {
      /* idem */
    }

    try {
      // at-40: a pendência é a TRANSCRIÇÃO do texto. A carga inicial trouxe os tipos do
      // catálogo, mas a API da Hinova nunca expôs o corpo dos termos — quem escreve as
      // cláusulas é o TI. Tipo sem texto não gera contrato.
      const templates = await pendenciasTemplates();
      if (templates.tiposSemTemplate > 0) {
        encontradas.push(
          `${templates.tiposSemTemplate} tipo(s) de termo sem texto cadastrado — não podem ser gerados; escreva as cláusulas em Administração de Termos`
        );
      }
    } catch {
      /* idem */
    }

    // Item 6: tipo de veículo novo no SGA sobre o qual ninguém decidiu nada.
    // Plano INATIVO não entra aqui — desativar já é a decisão de "não vendemos".
    try {
      const planos = await pendenciasPlanosCotacao();
      if (planos.tiposSemPlano > 0) {
        encontradas.push(
          `${planos.tiposSemPlano} tipo(s) de veículo do SGA sem plano de cotação definido — não aparecem para cotação; defina em Planos de Cotação`
        );
      }
    } catch {
      /* idem */
    }

    // Item 14: o SAAC nao sincroniza a FIPE (le o banco do fipe-atual), entao aqui
    // ele so ENXERGA o atraso — quem dispara o sync e o painel do SOAC.
    // ⚠️ INDISPONIVEL e a UNICA fonte deste banner que acende por falha de leitura:
    // sem o banco FIPE a selecao de modelo nao funciona, e isso e operacional. Falha
    // ao falar com o SAAC vem como DESCONHECIDA e segue silenciosa, como as demais.
    //
    // 🔴 PARCIAL NAO entra no banner (at-55, 08/09/2026 — pedido de Victor). Tres
    // razoes, e a primeira sozinha ja bastaria:
    //   1. A cadencia do fipe-atual e QUINZENAL, mas o estado PARCIAL acende 48h apos
    //      a ultima gravacao — entao ele fica aceso ~13 de cada 15 dias. E aviso
    //      permanente, que e o que treina a pessoa a ignorar o banner inteiro (mesmo
    //      motivo que tirou o plano inativo da at-43 e o e-mail da bike da at-46).
    //   2. Uma base FIPE nunca fica 100% num mes so: veiculo fora de linha para de ser
    //      republicado e congela no ultimo mes em que existiu. O limiar de 90% de
    //      predominancia pode ser inalcancavel por construcao.
    //   3. Nao ha acao possivel deste lado — o proprio texto mandava conferir no SOAC.
    //      Este banner e de pendencia ACIONAVEL do SAAC.
    // A informacao continua no card "Base FIPE" da tela /ti, que e read-only e e o
    // lugar certo para diagnostico. DEFASADA (2+ meses) segue no banner: aquilo e
    // anormal e contamina cota, valor protegido e adesao.
    try {
      const fipe = await getFipeStatus();
      if (fipe.situacao === 'DEFASADA') {
        encontradas.push(
          `${fipe.mensagem} A sincronização é do serviço fipe-atual — confira em SOAC → Sincronização FIPE.`
        );
      } else if (fipe.situacao === 'INDISPONIVEL') {
        encontradas.push(fipe.mensagem);
      }
    } catch {
      /* idem */
    }

    setPendencias(encontradas);
  }, [token]);

  const bannerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!token || !elevado) {
      setPendencias([]);
      return;
    }
    avaliar();
  }, [token, elevado, location.pathname, avaliar]);

  /**
   * A faixa fica ACIMA do container que rola (`.main-containt`, altura fixa
   * `100vh`) e o `body` tem `overflow: hidden` — o documento vira
   * "faixa + 100vh" sem ninguem para rolar a diferenca, e os ultimos px de
   * QUALQUER tela ficavam inalcancaveis (relatado por Victor em 03/09/2026,
   * com duas pendencias acesas o corte passou de 100px).
   *
   * A altura e publicada em `--altura-banner` e descontada no CSS do
   * container. Medida aqui, e nao no App, porque so a faixa conhece a
   * propria altura (que varia com o numero de pendencias) e o proprio ciclo
   * de vida — some da tela, a variavel volta a zero.
   */
  useEffect(() => {
    const raiz = document.documentElement;
    const faixa = bannerRef.current;

    if (!faixa) {
      raiz.style.setProperty('--altura-banner', '0px');
      return;
    }

    const medir = () =>
      raiz.style.setProperty('--altura-banner', `${faixa.offsetHeight}px`);

    medir();
    // A altura muda com a quantidade de pendencias e com a quebra de linha
    // no mobile — por isso observar, e nao medir so uma vez.
    const observer = new ResizeObserver(medir);
    observer.observe(faixa);

    return () => {
      observer.disconnect();
      raiz.style.setProperty('--altura-banner', '0px');
    };
  }, [elevado, pendencias.length]);

  if (!elevado || pendencias.length === 0) return null;

  return (
    <div
      // Marcador lido pelo NavGlobal (at-32) para descer o hambúrguer fixo até
      // abaixo desta faixa. Só identifica o elemento — nenhum comportamento aqui muda.
      ref={bannerRef}
      data-pendencias-banner=""
      style={{
        background: '#b45309',
        color: '#fff',
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        fontSize: 14,
        position: 'sticky',
        top: 0,
        zIndex: 1000,
      }}
    >
      {/*
        Sem emoji na interface (regra de Victor, 06/09/2026): o marcador de estado e
        TEXTO. Aqui havia um "⚠️" por linha, anterior a regra — trocado ao tocar no
        arquivo, como manda a convencao.
      */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {pendencias.map((p) => (
          <span key={p}>
            <strong>Atenção:</strong> {p}
          </span>
        ))}
      </div>
      <button
        type="button"
        onClick={() => navigate('/ti')}
        style={{
          background: '#fff',
          color: '#b45309',
          border: 'none',
          borderRadius: 4,
          padding: '4px 10px',
          cursor: 'pointer',
          fontWeight: 600,
          whiteSpace: 'nowrap',
        }}
      >
        Resolver
      </button>
    </div>
  );
};

export default PendenciasBanner;
